/**
 * CustomPagesTab - Admin tab for creating custom pages
 * Supports block-based content editing
 * 
 * Refactored: Extracted BlockEditor, FontSelector, and PagesList to /custom-pages/
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Save, X, FileText, Layout, Type, Image, Video, MousePointer, Code, Users } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";
import {
  BlockEditor,
  PageInfoForm,
  PagePublishSettings,
  PageSEOSettings,
  PagesList
} from "./custom-pages";

// Block type icons mapping
const BLOCK_ICONS = {
  hero: Layout, text: Type, image: Image, video: Video,
  gallery: Image, cta: MousePointer, columns: Layout,
  html: Code, sponsors: Users
};

export const CustomPagesTab = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "", slug: "", description: "",
    content_blocks: [], is_published: false,
    show_in_menu: false, page_title: "",
    meta_description: "", og_image: ""
  });

  useEffect(() => {
    fetchPages();
    fetchTemplates();
    fetchFonts();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/pages/admin/custom`, { headers: getAuthHeader() });
      setPages(res.data);
    } catch (err) { console.error("Error:", err); }
    finally { setLoading(false); }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/pages/templates`);
      setTemplates(res.data.templates || []);
    } catch (err) { console.error("Error:", err); }
  };

  const fetchFonts = async () => {
    try {
      const res = await axios.get(`${API}/fonts`);
      setFonts(res.data || []);
    } catch (err) { console.error("Error:", err); }
  };

  const resetForm = () => {
    setForm({
      title: "", slug: "", description: "",
      content_blocks: [], is_published: false,
      show_in_menu: false, page_title: "",
      meta_description: "", og_image: ""
    });
    setEditingPage(null);
  };

  const handleEdit = (page) => {
    setForm({
      title: page.title, slug: page.slug,
      description: page.description || "",
      content_blocks: page.content_blocks || [],
      is_published: page.is_published,
      show_in_menu: page.show_in_menu,
      page_title: page.page_title || "",
      meta_description: page.meta_description || "",
      og_image: page.og_image || ""
    });
    setEditingPage(page);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) { alert("Title and slug are required"); return; }
    setSaving(true);
    try {
      if (editingPage) {
        await axios.put(`${API}/pages/admin/custom/${editingPage.id}`, form, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/pages/admin/custom`, form, { headers: getAuthHeader() });
      }
      fetchPages();
      setShowEditor(false);
      resetForm();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save page");
    } finally { setSaving(false); }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm("Delete this page?")) return;
    try {
      await axios.delete(`${API}/pages/admin/custom/${pageId}`, { headers: getAuthHeader() });
      fetchPages();
    } catch (err) { console.error("Error:", err); }
  };

  const addBlock = (type) => {
    const template = templates.find(t => t.type === type);
    const newBlock = {
      id: Date.now().toString(),
      type, content: template?.default_content || {},
      order: form.content_blocks.length
    };
    setForm({ ...form, content_blocks: [...form.content_blocks, newBlock] });
  };

  const updateBlock = (index, block) => {
    const blocks = [...form.content_blocks];
    blocks[index] = block;
    setForm({ ...form, content_blocks: blocks });
  };

  const deleteBlock = (index) => {
    setForm({ ...form, content_blocks: form.content_blocks.filter((_, i) => i !== index) });
  };

  const moveBlock = (index, direction) => {
    const blocks = [...form.content_blocks];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    [blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]];
    setForm({ ...form, content_blocks: blocks });
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  // Page Editor View
  if (showEditor) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold uppercase text-white">
            {editingPage ? "Edit Page" : "New Custom Page"}
          </h2>
          <div className="flex items-center gap-2">
            <Button onClick={() => { setShowEditor(false); resetForm(); }} variant="ghost" className="text-zinc-400 hover:text-white">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-white text-black hover:bg-zinc-200">
              <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Page"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <PageInfoForm form={form} setForm={setForm} editingPage={editingPage} generateSlug={generateSlug} />

            {/* Content Blocks */}
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <h3 className="text-xs font-bold uppercase text-zinc-400 mb-4">Content Blocks</h3>
              
              <div className="space-y-3 mb-4">
                {form.content_blocks.map((block, index) => (
                  <BlockEditor
                    key={block.id}
                    block={block}
                    onChange={(b) => updateBlock(index, b)}
                    onDelete={() => deleteBlock(index)}
                    onMoveUp={() => moveBlock(index, -1)}
                    onMoveDown={() => moveBlock(index, 1)}
                    fonts={fonts}
                  />
                ))}
              </div>

              {/* Add Block Buttons */}
              <div className="flex flex-wrap gap-2">
                {templates.map((template) => {
                  const Icon = BLOCK_ICONS[template.type] || FileText;
                  return (
                    <button
                      key={template.type}
                      onClick={() => addBlock(template.type)}
                      className="flex items-center gap-2 px-3 py-2 bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 text-xs"
                    >
                      <Icon className="w-3 h-3" />{template.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <PagePublishSettings form={form} setForm={setForm} />
            <PageSEOSettings form={form} setForm={setForm} />
          </div>
        </div>
      </motion.div>
    );
  }

  // Pages List View
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white">Custom Pages</h2>
          <p className="text-zinc-500 text-sm mt-1">Create custom pages with your own content and URLs</p>
        </div>
        <Button onClick={() => setShowEditor(true)} className="bg-white text-black hover:bg-zinc-200">
          <Plus className="w-4 h-4 mr-2" />New Page
        </Button>
      </div>

      <PagesList pages={pages} onEdit={handleEdit} onDelete={handleDelete} onCreateNew={() => setShowEditor(true)} />
    </motion.div>
  );
};

export default CustomPagesTab;
